import { getAccessToken, getActiveCompany } from '@/lib/api';

/**
 * Client for the chat-driven migration wizard's SSE endpoint
 * (POST /api/agent/v1/migration/{id}/chat/stream) plus the small JSON helpers
 * for the migration agent surface. Adapted from CopilotPanel's streamChat.
 */

export interface CoaAccount {
  account_number: string;
  name: string;
  root_type: string;
  account_type?: string;
  is_group: boolean;
  parent_account_number?: string;
  account_currency?: string;
}

export interface SetupProfile {
  business_type: string;
  industry: string;
  employees: number;
  modules: string[];
  multicompany: boolean;
  fiscal_year_start: string;
  base_currency: string;
  legacy_system: string;
}

export interface ImportProposal {
  doctype: string;
  header_row: number;
  data_start_row: number;
  mapping: Record<string, string>;
  skip_columns?: string[];
  attachment_id: string;
  notes?: string;
}

export interface BalancesProposal {
  header_row: number;
  data_start_row: number;
  account_number_column?: string;
  label_column?: string;
  debit_column: string;
  credit_column: string;
  posting_date?: string;
  attachment_id: string;
  notes?: string;
}

export interface ReadinessCheck {
  id: string;
  label: string;
  pass: boolean;
  detail?: string;
  fix_url?: string;
}

export type MigrationSSE =
  | { kind: 'session';          data: { session_id: string } }
  | { kind: 'delta';            data: { content: string } }
  | { kind: 'tool_call';        data: { name: string; arguments: string } }
  | { kind: 'tool_result';      data: { name: string; ok: boolean } }
  | { kind: 'profile';          data: { profile: SetupProfile; complete: boolean } }
  | { kind: 'coa_proposal';     data: { accounts: CoaAccount[]; count: number } }
  | { kind: 'import_proposal';  data: ImportProposal }
  | { kind: 'balances_proposal'; data: BalancesProposal }
  | { kind: 'readiness';        data: { items: ReadinessCheck[]; passing: number; total: number } }
  | { kind: 'done';             data: { session_id: string; turn: number } }
  | { kind: 'error';            data: { message: string } };

export interface ChatAttachment {
  id: string;
  filename: string;
  grid_sample: string[][];
  total_rows: number;
}

export interface ChatBody {
  message: string;
  attachment?: ChatAttachment;
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const co = getActiveCompany();
  if (co) headers['X-Company-Id'] = co;
  return headers;
}

export async function streamMigrationChat(
  sessionId: string,
  body: ChatBody,
  onEvent: (ev: MigrationSSE) => void,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await fetch(`/api/agent/v1/migration/${sessionId}/chat/stream`, {
    method: 'POST',
    headers: authHeaders({ Accept: 'text/event-stream', 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok || !resp.body) {
    throw new Error((await resp.text()) || resp.statusText);
  }
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = parseSSE(raw);
      if (ev) onEvent(ev);
    }
  }
}

function parseSSE(raw: string): MigrationSSE | null {
  let event = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!event || !data) return null;
  try {
    return { kind: event, data: JSON.parse(data) } as MigrationSSE;
  } catch {
    return null;
  }
}

/** JSON helper for the migration agent endpoints (under /api/agent/v1). */
export async function agentFetch<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers = authHeaders({ Accept: 'application/json' });
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const r = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const t = await r.text();
  if (!r.ok) throw new Error(t || r.statusText);
  return t ? (JSON.parse(t) as T) : ({} as T);
}
